import { useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import type { SupplierAssessment, SupplierCertification, SupplierCheck, SupplyChainCheck } from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'
import { CriticitaBadge } from './BusinessApps'
import { Card, ErrorBanner, Spinner } from './common'
import { DataTable, type Column } from './DataTable'

/**
 * Assessments and certifications of a supplier, the NIS2 check of the
 * suppliers that matter (supplier_assessment_service): an assessment in date,
 * a security contact, no expired certification.
 */

const data = (iso: string | null) => (iso ? new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString(localeCorrente()) : '—')

export function etichettaMancanza(m: SupplierCheck['missing'][number]): string {
  switch (m) {
    case 'assessment': return t('valutazione in corso di validità')
    case 'security_contact': return t('contatto di sicurezza')
    case 'certification': return t('certificazioni in date')
  }
}

function etichettaTipo(k: SupplierAssessment['kind']): string {
  return { questionnaire: t('questionario'), audit: t('audit'), review: t('riesame') }[k]
}

function BadgeEsitoValutazione({ o }: { o: SupplierAssessment['outcome'] }) {
  const [tono, testo] = { ok: ['badge--ok', t('positiva')], conditions: ['badge--warn', t('con condizioni')], ko: ['badge--error', t('negativa')] }[o]
  return <span className={`badge ${tono}`}>{testo}</span>
}

export function ValutazioniFornitore({ supplierId, scrive }: { supplierId: string; scrive: boolean }) {
  const dati = useAsync(() => api.supplierAssessments(supplierId), [supplierId])
  const [nuovaV, setNuovaV] = useState(false)
  const [nuovaC, setNuovaC] = useState(false)
  const [v, setV] = useState({ assessed_at: new Date().toISOString().slice(0, 10), kind: 'questionnaire', outcome: 'ok', next_due: '', score: '', notes: '' })
  const [c, setC] = useState({ name: '', scope: '', issued: '', expires: '', url: '' })
  const [errore, setErrore] = useState<unknown>(null)

  async function esegui(fn: () => Promise<unknown>) {
    setErrore(null)
    try {
      await fn()
      await dati.reload()
      return true
    } catch (e) {
      setErrore(e)
      return false
    }
  }

  const colV: Column<SupplierAssessment>[] = [
    { key: 'assessed_at', header: t('Data'), value: (x) => x.assessed_at, render: (x) => data(x.assessed_at) },
    { key: 'kind', header: t('Tipo'), value: (x) => etichettaTipo(x.kind) },
    { key: 'outcome', header: t('Esito'), value: (x) => x.outcome, render: (x) => <BadgeEsitoValutazione o={x.outcome} /> },
    { key: 'score', header: t('Punteggio'), value: (x) => x.score ?? '' },
    { key: 'next_due', header: t('Prossima'), value: (x) => x.next_due ?? '', render: (x) => data(x.next_due) },
    { key: 'notes', header: t('Note'), value: (x) => x.notes },
    ...(scrive ? [{ key: 'azioni', header: '', sort: false, searchable: false, render: (x: SupplierAssessment) => (
      <button type="button" className="secondary" onClick={() => {
        if (window.confirm(t('Eliminare la valutazione?'))) void esegui(() => api.deleteSupplierAssessment(x.id))
      }}>{t('Elimina')}</button>) } as Column<SupplierAssessment>] : []),
  ]
  const colC: Column<SupplierCertification>[] = [
    { key: 'name', header: t('Certificazione'), render: (x) => x.url && /^https?:\/\//.test(x.url)
      ? <a href={x.url} target="_blank" rel="noreferrer">{x.name}</a> : x.name, value: (x) => x.name },
    { key: 'scope', header: t('Ambito'), value: (x) => x.scope },
    { key: 'expires', header: t('Scadenza'), value: (x) => x.expires ?? '', render: (x) => (
      <>{data(x.expires)} {x.expired ? <span className="badge badge--error">{t('scaduta')}</span>
        : x.expiring ? <span className="badge badge--warn">{t('in scadenza')}</span> : null}</>) },
    ...(scrive ? [{ key: 'azioni', header: '', sort: false, searchable: false, render: (x: SupplierCertification) => (
      <button type="button" className="secondary" onClick={() => {
        if (window.confirm(t('Eliminare la certificazione?'))) void esegui(() => api.deleteSupplierCertification(x.id))
      }}>{t('Elimina')}</button>) } as Column<SupplierCertification>] : []),
  ]

  if (dati.loading && !dati.data) return <Spinner />
  const d = dati.data
  return (
    <>
      <ErrorBanner error={errore ?? dati.error} />
      {d && (
        <Card title={t('Verifica della catena di fornitura (NIS2)')}>
          {d.check.matters ? (
            d.check.missing.length === 0
              ? <p style={{ margin: 0 }}><span className="badge badge--ok">{t('a posto')}</span> {t('Valutazione in corso di validità, contatto di sicurezza, nessuna certificazione scaduta.')}</p>
              : <p style={{ margin: 0 }}><span className="badge badge--warn">{t('manca')}</span> {d.check.missing.map(etichettaMancanza).join(', ')}</p>
          ) : (
            <p className="field__hint" style={{ margin: 0 }}>{t('Il fornitore non è critico né importante: la verifica NIS2 riguarda quelli che lo sono, ma le valutazioni restano utili.')}</p>
          )}
        </Card>
      )}
      <Card title={t('Valutazioni')} actions={scrive && !nuovaV ? <button type="button" onClick={() => setNuovaV(true)}>{t('Registra una valutazione')}</button> : undefined}>
        {nuovaV && (
          <div className="eagle-form contratto-form" style={{ marginBottom: 12 }}>
            <div className="eagle-report__riga">
              <label className="field" style={{ maxWidth: 170 }}><span className="field__label">{t('Data')}</span>
                <input type="date" value={v.assessed_at} onChange={(e) => setV({ ...v, assessed_at: e.target.value })} /></label>
              <label className="field" style={{ maxWidth: 170 }}><span className="field__label">{t('Tipo')}</span>
                <select value={v.kind} onChange={(e) => setV({ ...v, kind: e.target.value })}>
                  <option value="questionnaire">{t('questionario')}</option><option value="audit">{t('audit')}</option><option value="review">{t('riesame')}</option>
                </select></label>
              <label className="field" style={{ maxWidth: 170 }}><span className="field__label">{t('Esito')}</span>
                <select value={v.outcome} onChange={(e) => setV({ ...v, outcome: e.target.value })}>
                  <option value="ok">{t('positiva')}</option><option value="conditions">{t('con condizioni')}</option><option value="ko">{t('negativa')}</option>
                </select></label>
              <label className="field" style={{ maxWidth: 120 }}><span className="field__label">{t('Punteggio')}</span>
                <input type="number" min={0} max={100} value={v.score} onChange={(e) => setV({ ...v, score: e.target.value })} /></label>
              <label className="field" style={{ maxWidth: 170 }}><span className="field__label">{t('Prossima entro')}</span>
                <input type="date" value={v.next_due} onChange={(e) => setV({ ...v, next_due: e.target.value })} /></label>
            </div>
            <label className="field"><span className="field__label">{t('Note')}</span>
              <textarea rows={2} value={v.notes} onChange={(e) => setV({ ...v, notes: e.target.value })} /></label>
            <div className="eagle-form__azioni">
              <button type="button" onClick={() => void esegui(() => api.createSupplierAssessment(supplierId, {
                assessed_at: v.assessed_at, kind: v.kind as SupplierAssessment['kind'], outcome: v.outcome as SupplierAssessment['outcome'],
                next_due: v.next_due || null, score: v.score === '' ? null : Number(v.score), notes: v.notes,
              })).then((ok) => { if (ok) setNuovaV(false) })}>{t('Salva')}</button>
              <button type="button" className="secondary" onClick={() => setNuovaV(false)}>{t('Annulla')}</button>
            </div>
          </div>
        )}
        <DataTable rows={d?.assessments ?? []} columns={colV} rowKey={(x) => x.id} conteggio={{ nome: t('valutazioni') }}
                   emptyText={t('Nessuna valutazione registrata.')} />
      </Card>
      <Card title={t('Certificazioni')} actions={scrive && !nuovaC ? <button type="button" onClick={() => setNuovaC(true)}>{t('Aggiungi una certificazione')}</button> : undefined}>
        {nuovaC && (
          <div className="eagle-form contratto-form" style={{ marginBottom: 12 }}>
            <div className="eagle-report__riga">
              <label className="field"><span className="field__label">{t('Certificazione')}</span>
                <input value={c.name} onChange={(e) => setC({ ...c, name: e.target.value })} list="certificazioni-note" placeholder="ISO/IEC 27001" />
                <datalist id="certificazioni-note">
                  {['ISO/IEC 27001', 'SOC 2 Type II', 'ISO 9001', 'ISO 13485', 'ISO/IEC 27017', 'ISO/IEC 27018', 'TISAX', 'CSA STAR', 'ISO 22301'].map((x) => <option key={x} value={x} />)}
                </datalist></label>
              <label className="field" style={{ maxWidth: 170 }}><span className="field__label">{t('Emessa il')}</span>
                <input type="date" value={c.issued} onChange={(e) => setC({ ...c, issued: e.target.value })} /></label>
              <label className="field" style={{ maxWidth: 170 }}><span className="field__label">{t('Scade il')}</span>
                <input type="date" value={c.expires} onChange={(e) => setC({ ...c, expires: e.target.value })} /></label>
            </div>
            <div className="eagle-report__riga">
              <label className="field"><span className="field__label">{t('Ambito')}</span>
                <input value={c.scope} onChange={(e) => setC({ ...c, scope: e.target.value })} /></label>
              <label className="field"><span className="field__label">{t('Collegamento al certificato')}</span>
                <input value={c.url} onChange={(e) => setC({ ...c, url: e.target.value })} placeholder="https://" /></label>
            </div>
            <div className="eagle-form__azioni">
              <button type="button" disabled={!c.name.trim()} onClick={() => void esegui(() => api.createSupplierCertification(supplierId, {
                name: c.name, scope: c.scope, issued: c.issued || null, expires: c.expires || null, url: c.url,
              })).then((ok) => { if (ok) { setNuovaC(false); setC({ name: '', scope: '', issued: '', expires: '', url: '' }) } })}>{t('Salva')}</button>
              <button type="button" className="secondary" onClick={() => setNuovaC(false)}>{t('Annulla')}</button>
            </div>
          </div>
        )}
        <DataTable rows={d?.certifications ?? []} columns={colC} rowKey={(x) => x.id} conteggio={{ nome: t('certificazioni') }}
                   emptyText={t('Nessuna certificazione registrata.')} />
      </Card>
    </>
  )
}

/** The NIS2 report's card: art. 21(2)(d) on the suppliers that matter. */
export function CatenaFornitura({ dati }: { dati: SupplyChainCheck }) {
  const stato = {
    coperto: ['badge--ok', t('coperto')], parziale: ['badge--warn', t('parziale')],
    assente: ['badge--error', t('assente')], non_applicabile: ['badge--muted', t('nessun fornitore critico')],
  }[dati.status]
  return (
    <Card title={t('Sicurezza della catena di fornitura (art. 21, c. 2, d)')}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Misurata sui fornitori critici o importanti del registro fornitori (dalla criticità di ciò che sostengono): per ciascuno serve una valutazione in corso di validità, un contatto di sicurezza e nessuna certificazione scaduta.')}
      </p>
      <p><span className={`badge ${stato[0]}`}>{stato[1]}</span> {t('{ok} di {n} a posto', { ok: dati.ok, n: dati.total })}</p>
      {dati.suppliers.length > 0 && (
        <ul className="elenco-semplice">
          {dati.suppliers.map((s) => (
            <li key={s.id}>
              <Link to={`/suppliers/${s.id}?tab=valutazioni`}>{s.name}</Link> <CriticitaBadge value={s.criticality as never} />
              {s.missing.length === 0 ? <> · <span className="text-muted">{t('a posto')}</span></>
                : <> · {t('manca')}: {s.missing.map(etichettaMancanza).join(', ')}</>}
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}
